import { alpha, Components, createTheme, darken, lighten, ThemeOptions } from '@mui/material';
import { blue, cyan, green, grey, orange, pink, red, yellow } from '@mui/material/colors';
import type { PaletteOptions, SimplePaletteColorOptions, Theme, TypographyVariantsOptions } from '@mui/material/styles';
import { DataGridPremiumComponents } from '@mui/x-data-grid-premium/themeAugmentation';
import React, { useContext } from 'react';
import {
    COLOR_DEFAULT_BACKGROUND,
    COLOR_EDITING_DARK,
    COLOR_EDITING_LIGHT,
    COLOR_ERROR_DARK,
    COLOR_ERROR_LIGHT,
    COLOR_FOCUS_DARK,
    COLOR_FOCUS_LIGHT,
    COLOR_PRIMARY_DARK,
    COLOR_PRIMARY_LIGHT,
    COLOR_SUCCESS_DARK,
    COLOR_SUCCESS_LIGHT,
    COLOR_WARNING_DARK,
    COLOR_WARNING_LIGHT
} from './Colors';
import { getL10nDef } from './i18n/localization';
import { StylingContext } from './themeContext/StylingProvider';

const typography: TypographyVariantsOptions = {
    fontFamily: 'Lato, "Helvetica Neue", Arial, Helvetica, sans-serif',
    fontFamilyMonospace: 'Menlo, Consolas, Monaco, monospace'
};

declare module '@mui/material/styles' {
    interface TypographyVariants {
        fontFamilyMonospace: string;
    }
    interface TypographyVariantsOptions {
        fontFamilyMonospace?: string;
    }
}

declare module '@mui/material/styles' {
    export interface Theme {
        status: {
            danger: React.CSSProperties['color'];
        };
    }
    export interface ThemeOptions {
        status: {
            danger: React.CSSProperties['color'];
        };
    }
    export interface Palette {
        active?: Palette['primary'];
        inactive?: Palette['primary'];
        focus?: SimplePaletteColorOptions;
        selected?: SimplePaletteColorOptions;
        required?: SimplePaletteColorOptions;
        inviteInfo?: {
            titleText: string;
            descriptionText: string;
            backgroundColor: string;
        };
    }
    export interface PaletteOptions {
        active?: PaletteOptions['primary'];
        inactive?: PaletteOptions['primary'];
        focus?: SimplePaletteColorOptions;
        selected?: SimplePaletteColorOptions;
        required?: SimplePaletteColorOptions;
        inviteInfo?: {
            titleText: string;
            descriptionText: string;
            backgroundColor: string;
        };
    }

    export interface PaletteColor {
        darker?: string;
    }

    export interface SimplePaletteColorOptions {
        darker?: string;
    }
}

declare module '@mui/material/ButtonGroup' {
    export interface ButtonGroupPropsColorOverrides {
        active: true;
        inactive: true;
    }
}

declare module '@mui/material/Button' {
    export interface ButtonPropsColorOverrides {
        active: true;
        inactive: true;
    }
}

const getComponentOverrides = (
    isDark: boolean,
    palette: CustomPaletteOptions
): Components | DataGridPremiumComponents => {
    return {
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    minWidth: '1px',
                    width: '100%'
                }
            },
            defaultProps: {
                elevation: 5
            }
        },
        MuiAppBar: {
            defaultProps: {
                elevation: 5,
                color: 'default'
            }
        },
        MuiDrawer: {
            defaultProps: {
                elevation: 5
            }
        },
        MuiGrid: {
            styleOverrides: {
                root: {
                    // paddingLeft: '8px !important'
                    flexGrow: 1
                }
            }
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                groupedHorizontal: {
                    height: '32px',
                    minHeight: '32px'
                }
            }
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    '&.Mui-error': {
                        color: (palette.error as SimplePaletteColorOptions).main
                    },
                    '&.Mui-required': {
                        color: (palette.required as SimplePaletteColorOptions).main
                    }
                }
            }
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    '&.Mui-error': {
                        color: (palette.error as SimplePaletteColorOptions).main
                    },
                    '&.Mui-required': {
                        color: (palette.required as SimplePaletteColorOptions).main
                    }
                }
            }
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    '&.Mui-error': {
                        color: (palette.error as SimplePaletteColorOptions).main
                    },
                    '&.Mui-required': {
                        color: (palette.required as SimplePaletteColorOptions).main
                    },
                    '&.MuiFormHelperText': {
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis'
                    }
                }
            }
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    lineHeight: 1
                }
            }
        },
        MuiButtonBase: {
            styleOverrides: {
                root: {
                    '&.MuiChip-sizeSmall': {
                        height: '20px'
                    }
                }
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: isDark ? alpha(grey.A700, 0.7) : alpha(grey.A400, 0.7)
                    },
                    '&.Mui-selected': {
                        backgroundColor: (palette.selected as SimplePaletteColorOptions).main,
                        color: (palette.selected as SimplePaletteColorOptions).contrastText
                    },
                    '&.Mui-selected:hover': {
                        backgroundColor: alpha((palette.selected as SimplePaletteColorOptions).main, 0.9),
                        color: (palette.selected as SimplePaletteColorOptions).contrastText
                    }
                }
            }
        },
        MuiDataGrid: {
            styleOverrides: {
                root: {
                    '& .Mui-row': {
                        fontStyle: 'italic',
                        '&.Mui-hovered > * > [class|="MuiDataGrid-actionsCell"]': {
                            visibility: 'visible'
                        }
                    },
                    '& .MuiDataGrid-row.aggregatedRow': {
                        backgroundColor: `${palette.background.paper} !important`,
                        position: 'absolute'
                    },
                    '& .MuiDataGrid-row.groupedRow': {
                        backgroundColor: palette.focus[isDark ? 'dark' : 'main'],
                        '&:hover, &.Mui-hovered, &:not(.Mui-selected):focus, &:not(.Mui-selected):focus-within': {
                            backgroundColor: palette.focus[isDark ? 'dark' : 'main']
                        }
                    },
                    '& .MuiDataGrid-row.tableRow .MuiDataGrid-cell--pinnedRight': {
                        backgroundColor: 'transparent',
                        borderLeft: 'none'
                    },
                    '& .MuiDataGrid-row:last-of-type': {
                        borderBottom: '1px solid var(--rowBorderColor)'
                    },
                    '& .MuiDataGrid-row:first-of-type:last-of-type': {
                        borderTop: 0
                    },
                    '& .MuiDataGrid-pinnedRows .MuiDataGrid-row': {
                        backgroundColor: 'inherit',
                        '&:hover, &.Mui-hovered': {
                            backgroundColor: 'inherit'
                        },
                        '&:hover .MuiDataGrid-cell, &.Mui-hovered .MuiDataGrid-cell': {
                            backgroundColor: palette.background.paper
                        },
                        '&:hover .MuiDataGrid-cell--editing, &.Mui-hovered .MuiDataGrid-cell--editing': {
                            backgroundColor: palette.background.paper
                        }
                    },

                    '& .MuiDataGrid-container--top [role=row]': {
                        backgroundColor: palette.background.paper
                    },
                    '& .MuiDataGrid-scrollbarFiller.MuiDataGrid-scrollbarFiller--pinnedRight': {
                        backgroundColor: palette.background.paper
                    },
                    '& .MuiDataGrid-filler': {
                        display: 'none'
                    },

                    '& .MuiDataGrid-row': {
                        '& .MuiDataGrid-cell--pinnedRight:hover': {
                            backgroundColor: 'transparent'
                        },
                        '.MuiDataGrid-cell--textBold': {
                            '.MuiTypography-root, input': {
                                fontWeight: 'bold'
                            }
                        },
                        '.MuiDataGrid-cell--textRight': {
                            '.MuiTypography-root, .MuiInputBase-input': {
                                width: '100%',
                                textAlign: 'right',
                                paddingRight: '1px'
                            }
                        },
                        '.MuiDataGrid-cell--textLeft': {
                            display: 'flex',
                            alignItems: 'center'
                        },
                        // row being edited
                        '&.MuiDataGrid-row--editing': {
                            backgroundColor: isDark ? COLOR_EDITING_DARK : COLOR_EDITING_LIGHT,
                            color: isDark ? 'white' : 'black',
                            boxShadow: 'none',
                            '&:hover, &.Mui-hovered': {
                                backgroundColor: isDark ? COLOR_EDITING_DARK : COLOR_EDITING_LIGHT
                            }
                        },
                        '.MuiDataGrid-cell--editing': {
                            '.MuiAutocomplete-root': {
                                fieldset: {
                                    border: 'none'
                                }
                            },
                            '&.isEmpty': {
                                background: `linear-gradient(to top right,transparent 50%,${
                                    (palette.error as SimplePaletteColorOptions).main
                                } 50.1%) top right/10px 10px no-repeat,transparent;`
                            }
                        },
                        '& .MuiDataGrid-actionsCell': {
                            gap: 0
                        }
                    }
                }
            }
        },
        MuiMenu: {
            defaultProps: {
                slotProps: {
                    paper: {
                        sx: {
                            width: 'auto'
                        }
                    }
                }
            },
            styleOverrides: {
                paper: {
                    width: 'auto'
                }
            }
        },
        MuiPopover: {
            defaultProps: {
                slotProps: {
                    paper: {
                        sx: {
                            width: 'auto'
                        }
                    }
                }
            },
            styleOverrides: {
                paper: {
                    width: 'auto '
                }
            }
        }
    };
};

interface CustomPaletteOptions extends PaletteOptions {
    active?: SimplePaletteColorOptions;
    required?: SimplePaletteColorOptions;
    selected?: SimplePaletteColorOptions;
    inactive?: SimplePaletteColorOptions;
    focus?: SimplePaletteColorOptions;
    inviteInfo?: {
        titleText: string;
        descriptionText: string;
        backgroundColor: string;
    };
}

const getDarkPalette = (): CustomPaletteOptions => {
    return {
        mode: 'dark',
        primary: {
            main: COLOR_PRIMARY_DARK,
            light: lighten(COLOR_PRIMARY_DARK, 0.2),
            dark: darken(COLOR_PRIMARY_DARK, 0.2)
        },
        active: {
            main: blue.A100,
            contrastText: 'rgba(255, 255, 255, 1)'
        },
        required: {
            main: lighten(COLOR_PRIMARY_DARK, 0.2)
        },
        selected: {
            main: grey.A700,
            contrastText: 'rgba(255, 255, 255, 1)'
        },
        inactive: {
            main: grey.A400,
            contrastText: grey.A700
        },
        text: {
            primary: grey[50],
            secondary: 'rgba(255, 255, 255, 0.7)',
            disabled: grey[600]
        },
        background: {
            default: grey[900],
            paper: grey[800]
        },
        success: {
            main: COLOR_SUCCESS_DARK
        },
        error: {
            main: COLOR_ERROR_DARK,
            light: lighten(COLOR_ERROR_DARK, 0.2),
            dark: darken(COLOR_ERROR_DARK, 0.2)
        },
        warning: {
            main: COLOR_WARNING_DARK
        },
        focus: {
            main: grey[800],
            dark: grey[600]
        },
        inviteInfo: {
            titleText: '#000000DE',
            descriptionText: '#000000B2',
            backgroundColor: '#EDE7F6'
        }
    };
};

const getLightPalette = (): CustomPaletteOptions => {
    return {
        mode: 'light',
        primary: {
            main: COLOR_PRIMARY_LIGHT,
            light: lighten(COLOR_PRIMARY_LIGHT, 0.2),
            dark: darken(COLOR_PRIMARY_LIGHT, 0.2)
        },
        secondary: {
            main: 'rgba(236, 99, 55, 1)'
        },
        required: {
            main: lighten(COLOR_PRIMARY_LIGHT, 0.2)
        },
        selected: {
            main: grey.A400,
            contrastText: 'rgba(0, 0, 0, 1)'
        },
        active: {
            main: 'rgba(225, 241, 249, 1)',
            contrastText: 'rgba(2, 136, 209, 1)'
        },
        inactive: {
            main: grey.A700,
            contrastText: grey.A400
        },
        text: {
            primary: grey[900],
            secondary: 'rgba(0, 0, 0, 0.7)',
            disabled: grey[500]
        },
        background: {
            default: COLOR_DEFAULT_BACKGROUND,
            paper: 'rgba(255, 255, 255, 1)'
        },
        success: {
            main: COLOR_SUCCESS_LIGHT
        },
        error: {
            main: COLOR_ERROR_LIGHT,
            light: lighten(COLOR_ERROR_LIGHT, 0.2),
            dark: darken(COLOR_ERROR_LIGHT, 0.2)
        },
        warning: {
            main: COLOR_WARNING_LIGHT
        },
        focus: {
            main: COLOR_FOCUS_LIGHT,
            dark: COLOR_FOCUS_DARK
        },
        inviteInfo: {
            titleText: '#000000DE',
            descriptionText: '#000000B2',
            backgroundColor: '#EDE7F6'
        }
    };
};

const getShape = () => {
    return {
        borderRadius: 10
    };
};

export const getTheme = (isDark, locale): Theme => {
    const darkPalette = getDarkPalette();
    const lightPalette = getLightPalette();
    const shape = getShape();
    const l10n = getL10nDef(locale);
    const translations = [l10n.muiCore, l10n.muiPickers, l10n.muiGrid];
    return isDark
        ? createTheme(
              {
                  components: getComponentOverrides(isDark, darkPalette),
                  status: {
                      danger: red.A400
                  },
                  palette: darkPalette,
                  typography: typography,
                  shape: shape
              } as ThemeOptions,
              ...translations
          )
        : createTheme(
              {
                  components: getComponentOverrides(isDark, lightPalette),
                  status: {
                      danger: '#C13F38'
                  },
                  palette: lightPalette,
                  typography: typography,
                  shape: shape
              } as ThemeOptions,
              ...translations
          );
};

export const useStyling = () => {
    const stylingContextValue = useContext(StylingContext);
    if (stylingContextValue === null || stylingContextValue === undefined) {
        throw new Error('useStyling must be used within a StylingProvider');
    }
    return stylingContextValue;
};

export const TIMELINE_COLORS_BY_STATUS: Record<string, string> = {
    Unavailable: grey['400'],
    Booked: green['400'],
    Hold: cyan['400'],
    '1st Hold': blue['400'],
    '2nd Hold': orange['400'],
    Inquiry: yellow['600'],
    Lead: pink['400'],
    'Closed Lost': '#A9B6FB',
    Quoted: '#FECEA8'
};

export const TIMELINE_COLORS_BY_EVENT_TYPE: Record<string, string> = {
    'Non-Working': grey['400'],
    Milestone: green['400'],
    testing: cyan['400'],
    Phase: blue['400'],
    Task: orange['400'],
    Activity: yellow['600'],
    Scene: pink['400']
};
